import { useMediaQuery, useTheme } from '@mui/material';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';

import Preloader from '../../../components/ui/Preloader/Preloader';

import { actions as cartActions } from '../../../store/ducks/cart.duck';
import { actions as categoriesActions } from '../../../store/ducks/categories.duck';
import { actions as productCatalogActions } from '../../../store/ducks/product-catalog.duck';
import { actions as productTypesActions } from '../../../store/ducks/productType.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import { IAppState } from '../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { ICategoryItem } from '../categories/interfaces';
import { useStylesProductCatalog } from './hooks/useStyles';

const RenderCatalogTree = ({
  item,
  onPressCategory,
}: {
  item: ICategoryItem;
  onPressCategory?: (e: any) => void;
}) => {
  const { classes } = useStylesProductCatalog();

  return item.children?.length ? (
    <div>
      {item.children
        .filter(j => j?.active)
        .map(j => (
          <Fragment key={j.id}>
            <p className={classes.categoryItem} onClick={() => onPressCategory?.(j)}>
              {j.name} <span>({j.children?.length || 1})</span>
            </p>
          </Fragment>
        ))}
    </div>
  ) : null;
};

const ProductNewCatalog: React.FC<TPropsFromRedux> = ({
  search,
  sortType,
  loading,
  page,
  perPage,
  deleteError,
  loadingMe,
  catalogCategories,
  clearProducts,
  clearDel,
  fetchMe,
  clearMe,
  fetchCatalogCategories,
  setViewType,
  setSearch,
  isSearching,
  fetchTypeById,
  clearParams,
  fetchType,
  clearAddProduct,
  loadingTypes,
  categoriesLoading,
  categoryIdActive,
  setCategoryIdActive,
  clearRequest,
  setSearchText,
}) => {
  const { classes } = useStylesProductCatalog();

  const intl = useIntl();
  const location = useLocation() as any;
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [columns, setColumns] = useState(1);
  const navigate = useNavigate();
  const [categoryList, setCategoryList] = useState<any[][]>([[], [], []]);
  const isTabletScreen = useMediaQuery(theme.breakpoints.down(950));
  const isMobileScreen = useMediaQuery(theme.breakpoints.down(560));

  const isRootCategories = useMemo(() => {
    if (
      categoryList?.[0]?.[0]?.id === catalogCategories?.[0]?.id &&
      categoryList?.[1]?.[0]?.id === catalogCategories?.[1]?.id &&
      categoryList?.[2]?.[0]?.id === catalogCategories?.[2]?.id
    )
      return true;
    return false;
  }, [catalogCategories, categoryList]);

  useEffect(() => {
    if (isMobileScreen) setColumns(1);
    else if (isTabletScreen) setColumns(2);
    else setColumns(3);
  }, [isTabletScreen, isMobileScreen]);

  useEffect(() => {
    setCategoryIdActive(0);
    fetchMe();
    clearParams();
    fetchType();
    fetchCatalogCategories({ with_products: true, only_active: 1 });
    setViewType('grid');

    return () => {
      clearMe();
      clearProducts();
      clearDel();
      clearAddProduct();
      clearParams();
    };
  }, []);

  const getResult = (categories: any) => {
    let result: any[][] = [[], [], []];
    categories?.forEach((item: any, index: number) => {
      return result[index % 3].push(item);
    });
    return result;
  };

  useEffect(() => {
    const result = getResult(catalogCategories);
    setCategoryList(result || [[], [], []]);
  }, [catalogCategories]);

  useEffect(() => {
    if (location?.state?.categoryId) {
      setSearch(true);
      setCategoryIdActive(location.state.categoryId);
    }
    if (location?.state?.productTypeId) {
      fetchTypeById({ id: location.state.productTypeId, filters: true });
    }
  }, [location]);

  useEffect(() => {
    if (search) setSearch(true);
  }, [sortType, search, page, perPage]);

  useEffect(() => {
    if (deleteError) {
      enqueueSnackbar(deleteError, { variant: 'error' });
      clearDel();
    }
  }, [deleteError]);

  const handleBackPress = useCallback(() => {
    setCategoryList(getResult(catalogCategories));
    setCategoryIdActive(0);
  }, []);

  setLayoutFooter({ show: true });

  const findPathById = (arr: any[], targetId: number | undefined | null, path = []): any => {
    if (!arr.length || !targetId) {
      return ['Нет даты'];
    }

    for (const item of arr) {
      const newPath: any = [...path, { name: item.name, id: item.id }];

      if (item.id === targetId) {
        return newPath;
      }

      if (item.children.length > 0) {
        const foundPath = findPathById(item.children, targetId, newPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }

    return null;
  };

  setLayoutSubheader(
    categoryIdActive
      ? {
          breadcrumb: [
            {
              name: intl.formatMessage({ id: 'PRODUCT.CATALOG.ALL.CATEGORIES' }),
            },
            ...(findPathById(catalogCategories, categoryIdActive) || []),
          ].map((element: any) => {
            return {
              title:
                element.name?.length > 30 ? `${element.name.slice(0, 30)} ... ` : element.name,
              hoverTitle: element.name,
              root: true,
              id: element.id,
              children: element.children,
              page:
                element.name === intl.formatMessage({ id: 'PRODUCT.CATALOG.ALL.CATEGORIES' })
                  ? 'products/catalog/categories'
                  : 'products/catalog',
              onClickCategory: (id: number) => {
                setSearchText('');
                setCategoryIdActive(id);
              },
              click: () => {
                if (
                  element.name === intl.formatMessage({ id: 'PRODUCT.CATALOG.ALL.CATEGORIES' })
                ) {
                  setCategoryList(getResult(catalogCategories));
                  setCategoryIdActive(0);
                } else {
                  setCategoryIdActive(element.id || 0);
                }
              },
              isActive: element.id === categoryIdActive,
            };
          }),
        }
      : {
          breadcrumb: [
            {
              title: intl.formatMessage({ id: 'PRODUCT.CATALOG.ALL.CATEGORIES' }),
              page: 'products/catalog/categories',
              root: true,
              translate: 'PRODUCT.CATALOG.ALL.CATEGORIES',
              isActive: true,
            },
          ],
        }
  );

  const handlePressCategoryTitle = (category: any) => {
    setCategoryIdActive(category.id);
    setSearchText('');
    clearRequest();
    navigate('/products/catalog');
  };

  const onPressCategory = (category: any) => {
    setCategoryIdActive(category.id);
    if (category.children.length === 0) {
      clearRequest();
      setSearchText('');
      navigate('/products/catalog');
      return;
    }
    setCategoryList(getResult(category.children));
    window.scrollTo(0, 0);
  };

  if (categoriesLoading || loadingMe || loadingTypes || loading) {
    return <Preloader />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.productsCol}>
        <div className={clsx(classes.card, { [classes.revers]: isSearching })}>
          {!categoryList?.length ? (
            <Preloader />
          ) : (
            <div>
              {!isRootCategories ? (
                <p className={classes.backTitle} onClick={handleBackPress}>
                  <ArrowBackIcon />
                  {intl.formatMessage({
                    id: 'AUTH.BUTTON.BACK',
                  })}
                </p>
              ) : null}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${columns}, 1fr)`,
                  gap: '20px',
                }}
              >
                {categoryList.map(e => {
                  return (
                    <div>
                      {e
                        ?.filter((cat: any) => cat?.active)
                        .map((cat: any) => (
                          <div
                            key={cat.id}
                            style={{
                              backgroundColor: '#f7f7f7',
                              borderRadius: '8px',
                              padding: '16px',
                              margin: '16px 0',
                            }}
                          >
                            <p
                              className={classes.subTitle}
                              onClick={() => handlePressCategoryTitle(cat)}
                            >
                              {cat.name}
                            </p>
                            <RenderCatalogTree item={cat} onPressCategory={onPressCategory} />
                          </div>
                        ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const connector = connect(
  (state: IAppState) => ({
    loading: state.productsCatalog.loading,
    page: state.productsCatalog.page,
    perPage: state.productsCatalog.per_page,
    deleteError: state.productsCatalog.delError,
    isSearching: state.productsCatalog.isSearching,

    catalogCategories: state.categories.catalogCategories,
    categoriesLoading: state.categories.categoriesLoading,

    loadingMe: state.profile.loading,

    loadingTypes: state.productTypes.loading,
    sortType: state.productsCatalog.sortType,
    search: state.productsCatalog.search,
    categoryIdActive: state.productsCatalog.categoryIdActive,
  }),
  {
    clearProducts: productCatalogActions.clearProducts,
    clearDel: productCatalogActions.clearDel,
    clearRequest: productCatalogActions.clearRequest,
    setSearchText: productCatalogActions.setSearching,
    setViewType: productCatalogActions.setViewType,
    setSearch: productCatalogActions.setSearch,
    setCategoryIdActive: productCatalogActions.setCategoryIdActive,

    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,

    fetchCatalogCategories: categoriesActions.fetchCatalogCategories,

    fetchTypeById: productTypesActions.fetchParamsByIdRequest,
    clearParams: productTypesActions.clearParams,
    fetchType: productTypesActions.fetchRequest,

    clearAddProduct: cartActions.clearAddProduct,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ProductNewCatalog);
